import type { SportsMatch } from '../account/api';

export interface LeagueGroup {
  /** The league heading; '' for matches without a known competition (shown as "Other"). */
  league: string;
  matches: SportsMatch[];
}

type Listed = Pick<SportsMatch, 'competition' | 'league' | 'leagueRank' | 'live' | 'startsAt'>;

/**
 * Today's matches under their league (#86): the viewer's favourite leagues
 * first, then the most popular (the relay's rank), then "Other" for every
 * league that isn't ranked or isn't known. Within a league, live matches
 * first, then by kick-off. Older servers send no league or rank: their
 * matches are grouped by competition, sorted by name.
 */
export function groupByLeague<T extends Listed>(matches: T[], favourites: readonly string[] = []): { league: string; matches: T[] }[] {
  const groups = new Map<string, { league: string; rank: number | null; matches: T[] }>();
  for (const match of matches) {
    // Leagues the relay doesn't rank all go under "Other" (Abel, #86).
    const league = match.leagueRank === null ? '' : (match.league ?? match.competition).trim();
    const group = groups.get(league) ?? { league, rank: null, matches: [] };
    const rank = match.leagueRank ?? null;
    if (rank !== null && (group.rank === null || rank < group.rank)) group.rank = rank;
    group.matches.push(match);
    groups.set(league, group);
  }
  const order = (group: { league: string; rank: number | null }) =>
    group.league === '' ? 3 : favourites.includes(group.league) ? 0 : group.rank === null ? 2 : 1;
  return [...groups.values()]
    .sort((a, b) => order(a) - order(b) || (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.league.localeCompare(b.league))
    .map(({ league, matches: listed }) => ({
      league,
      matches: [...listed].sort((a, b) => Number(b.live) - Number(a.live) || (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity)),
    }));
}

const FAVOURITES_KEY = 'viva.favouriteLeagues';

/** The leagues this device has starred. Storage can be unavailable (private mode, TV quirks). */
export function readFavouriteLeagues(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(FAVOURITES_KEY) ?? '[]') as unknown;
    return Array.isArray(saved) ? saved.filter((league): league is string => typeof league === 'string' && league !== '') : [];
  } catch {
    return [];
  }
}

export function saveFavouriteLeagues(leagues: string[]): void {
  try {
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(leagues));
  } catch {
    // Not remembering them only costs convenience.
  }
}
